import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { posts, CoverDefs, HeroArt, formatDate } from "../../data/blogData";
import "../../styles/blog-detail.css";

const Icon = {
  calendar: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="17" height="15" rx="3" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  ),
  clock: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  arrow: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  left: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  ),
  crumb: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  link: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
      <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
    </svg>
  ),
  check: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.7 2.7L16 9.5" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.5h4V21H3zm6.5 0h3.8v1.6h.1c.5-1 1.8-1.9 3.7-1.9 4 0 4.7 2.6 4.7 6V21h-4v-5.1c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4z" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 22v-8.2h2.8l.5-3.4h-3.3V8.3c0-1 .4-1.8 1.9-1.8h1.5V3.4c-.3 0-1.3-.1-2.4-.1-2.5 0-4.2 1.5-4.2 4.3v2.8H7.5v3.4h2.8V22z" />
    </svg>
  ),
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* Renders one block of article content */
function Block({ block }) {
  switch (block.type) {
    case "lead":
      return <p className="gt9-lead">{block.text}</p>;
    case "h2":
      return <h2 id={slugify(block.text)}>{block.text}</h2>;
    case "p":
      return <p>{block.text}</p>;
    case "ul":
      return (
        <ul>
          {block.items.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="gt9-quote">
          <p>{block.text}</p>
          {block.cite && <cite>{block.cite}</cite>}
        </blockquote>
      );
    case "callout":
      return (
        <aside className="gt9-callout">
          <h3>
            {Icon.check}
            {block.title}
          </h3>
          <ul>
            {block.items.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </aside>
      );
    case "steps":
      return (
        <div className="gt9-steps">
          {block.items.map((s) => (
            <div className="gt9-step" key={s.title}>
              <span>{s.label}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function ShareButtons({ title }) {
  const [copied, setCopied] = useState(false);
  const url = encodeURIComponent(
    typeof window !== "undefined" ? window.location.href : "",
  );
  const text = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (e) {
      /* clipboard can be blocked; still show feedback */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="gt9-share">
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Share on LinkedIn"
      >
        {Icon.linkedin}
      </a>
      <a
        href={`https://twitter.com/intent/tweet?url=${url}&text=${text}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Share on X"
      >
        {Icon.x}
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Share on Facebook"
      >
        {Icon.facebook}
      </a>
      <button
        type="button"
        className={copied ? "is-done" : ""}
        onClick={copy}
        aria-label={copied ? "Link copied" : "Copy link"}
      >
        {copied ? Icon.check : Icon.link}
      </button>
    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const index = posts.findIndex((p) => p.slug === slug);
  const post = posts[index];

  const articleRef = useRef(null);
  const progressRef = useRef(null);
  const [active, setActive] = useState("");

  const blocks = post?.content;
  const headings = useMemo(
    () =>
      (blocks || [])
        .filter((b) => b.type === "h2")
        .map((b) => ({ id: slugify(b.text), text: b.text })),
    [blocks],
  );

  // start every article at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const el = articleRef.current;
      const bar = progressRef.current;
      if (!el || !bar) return;
      const r = el.getBoundingClientRect();
      const total = Math.max(r.height - window.innerHeight * 0.6, 1);
      const p = Math.min(
        1,
        Math.max(0, (-r.top + window.innerHeight * 0.2) / total),
      );
      bar.style.transform = `scaleX(${p})`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [slug]);

  // highlight the current section in "In this article"
  useEffect(() => {
    if (!headings.length || !("IntersectionObserver" in window))
      return undefined;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-20% 0px -65% 0px" },
    );
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [headings]);

  if (!post) return <Navigate to="/blogs" replace />;

  const tags = post.keywords || [];

  return (
    <main className="gt9-art">
      <CoverDefs />
      <div className="gt9-progress" ref={progressRef} aria-hidden="true" />

      {/* Hero */}
      <section className="gt9-art__hero">
        <div className="gt9-container">
          <ol className="gt9-crumbs" aria-label="Breadcrumb">
            <li>
              <Link to="/">Home</Link>
              {Icon.crumb}
            </li>
            <li>
              <Link to="/blogs">Blog &amp; news</Link>
              {Icon.crumb}
            </li>
            <li aria-current="page">{post.tag}</li>
          </ol>
          <Link
            className="gt9-art__pill"
            to={`/blogs?topic=${encodeURIComponent(post.tag)}`}
          >
            {post.tag}
          </Link>
          <h1 className="gt9-art__title">{post.title}</h1>
          <div className="gt9-art__meta">
            <span className="gt9-art__by">
              <i>G9</i>
              <b>Global Tech9 LLC Team</b>
            </span>
            <span>
              {Icon.calendar}
              <time dateTime={post.iso}>{formatDate(post.iso)}</time>
            </span>
            <span>
              {Icon.clock}
              {post.read} min read
            </span>
          </div>
        </div>
      </section>

      <div className="gt9-art__body">
        <div className="gt9-container">
          {/* Hero image: set `heroImage` (or `image`) on the post; the illustrated artwork is only a fallback */}
          <figure
            className="gt9-art__figure"
            style={{ marginBottom: 0, marginTop: "20px" }}
          >
            {post.heroImage || post.image ? (
              <img
                src={post.heroImage || post.image}
                alt={post.title}
                width="1800"
                height="720"
              />
            ) : (
              <HeroArt type={post.cover} />
            )}
          </figure>

          <div className="gt9-art__layout">
            <article ref={articleRef}>
              <div className="gt9-prose">
                {blocks ? (
                  blocks.map((b, i) => <Block key={i} block={b} />)
                ) : (
                  <>
                    <p className="gt9-lead">{post.text}</p>
                    <p>
                      This article body is a placeholder. Add a{" "}
                      <code>content</code> array to this post in blogData.jsx,
                      or load the full article from your CMS or API.
                    </p>
                  </>
                )}
              </div>

              <div className="gt9-art__foot">
                <div className="gt9-art__row">
                  <div className="gt9-tags">
                    <Link to={`/blogs?topic=${encodeURIComponent(post.tag)}`}>
                      {post.tag}
                    </Link>
                    {tags.map((k) => (
                      <Link key={k} to={`/blogs?q=${encodeURIComponent(k)}`}>
                        {k}
                      </Link>
                    ))}
                  </div>
                  <div
                    className="gt9-share-row"
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <b
                      style={{
                        font: '700 14px "Manrope",sans-serif',
                        color: "#16223f",
                      }}
                    >
                      Share
                    </b>
                    <ShareButtons title={post.title} />
                  </div>
                </div>

                <div className="gt9-author">
                  <i>G9</i>
                  <div>
                    <b>Written by the Global Tech9 LLC team</b>
                    <p>
                      Engineers and consultants who look after infrastructure,
                      cloud and security for growing businesses.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            <aside className="gt9-aside" aria-label="Article tools">
              {headings.length > 0 && (
                <div className="gt9-panel">
                  <h3>In this article</h3>
                  <ol
                    className="gt9-toc"
                    style={{ listStyle: "none", margin: 0, padding: 0 }}
                  >
                    {headings.map((h) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className={active === h.id ? "is-active" : ""}
                        >
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="gt9-panel">
                <h3>Share this article</h3>
                <ShareButtons title={post.title} />
              </div>
              <div className="gt9-cta2">
                <h3>Want a security review?</h3>
                <p>
                  Our team can assess your setup and give you a practical plan
                  that fits your size and budget.
                </p>
                <Link to="/contact-us">Talk to an expert {Icon.arrow}</Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </main>
  );
}
